import { forwardRef, memo, ReactNode, useRef } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
// Import Types
import type { RouteModel } from 'types';
// Import Hook
import useGoToRoute from 'hooks/GoToRoute';
// Import UI Components
import { Icon, Tooltip, useDeviceType, useEllipsisDetection } from '@geneui/components';

export const optionConfig = {
    color: ['default', 'hero'],
    border: ['none', 'top', 'bottom'],
    sticky: ['none', 'top', 'bottom'],
    titlePosition: {
        start: 'start',
        center: 'center',
        end: 'end',
    },
} as const;

export const screenTypes = ['desktop', 'mobile'] as const;

interface Props {
    routeModel: RouteModel;
    title?: string;
    description?: string;
    className?: string;
    icon?: string;
    active?: boolean;
    checkMark?: boolean;
    forwardMark?: boolean;
    leftCustomElement?: ReactNode;
    rightCustomElement?: ReactNode;
    assignedValue?: string;
    content?: ReactNode;
    disabled?: boolean;
    color?: (typeof optionConfig.color)[number];
    border?: (typeof optionConfig.border)[number];
    sticky?: (typeof optionConfig.sticky)[number];
    screenType?: (typeof screenTypes)[number];
    titlePosition?: keyof typeof optionConfig.titlePosition;
    onClick?: () => void;
}

const NavigationOption = forwardRef<HTMLAnchorElement, Props>((props, ref) => {
    const {
        routeModel,
        title,
        description,
        className,
        icon,
        color = optionConfig.color[0],
        active,
        checkMark,
        forwardMark,
        leftCustomElement,
        rightCustomElement,
        assignedValue,
        border = optionConfig.border[0],
        sticky = optionConfig.sticky[0],
        content,
        screenType = screenTypes[0],
        disabled,
        titlePosition = optionConfig.titlePosition.start,
        onClick,
    } = props;
    const { getRoutePath } = useGoToRoute();
    const titleRef = useRef<HTMLDivElement>(null);
    const hasTooltip = useEllipsisDetection(titleRef);
    const descriptionRef = useRef(null);
    const hasDescriptionTooltip = useEllipsisDetection(descriptionRef);
    const { isMobile } = useDeviceType(screenType);

    return (
        <NavLink
            ref={ref}
            to={getRoutePath(routeModel)}
            className={classNames('option-container', className, `border-${border}`, `sticky-${sticky}`, `c-${color}`, {
                'mobile-view': isMobile,
                'option-container-relative': titlePosition === optionConfig.titlePosition.center,
                disabled,
            })}
            activeClassName="active"
            isActive={() => !!active}
            onClick={onClick}
        >
            {content}
            {(icon || leftCustomElement) && (
                <div className="option-left-elements">
                    {icon && <Icon type={icon} />}
                    {leftCustomElement}
                </div>
            )}
            <ul className="option-texts">
                <li>
                    {title && (
                        <Tooltip text={title ?? ''} isVisible={hasTooltip}>
                            <div
                                className={classNames('option-title', 'ellipsis-text', {
                                    'option-title-center': titlePosition === optionConfig.titlePosition.center,
                                    'option-title-end': titlePosition === optionConfig.titlePosition.end,
                                })}
                                ref={titleRef}
                            >
                                {title}
                            </div>
                        </Tooltip>
                    )}
                    {description && (
                        <Tooltip text={description} isVisible={hasDescriptionTooltip}>
                            <div ref={descriptionRef} className="option-description ellipsis-text">
                                {description}
                            </div>
                        </Tooltip>
                    )}
                </li>
                {assignedValue && <li className="assigned-value">{assignedValue}</li>}
            </ul>
            {(checkMark || forwardMark || rightCustomElement) && (
                <div className="option-right-elements">
                    {checkMark && <Icon type="bc-icon-selected" />}
                    {forwardMark && <Icon type="bc-icon-arrow-right" />}
                    {rightCustomElement}
                </div>
            )}
        </NavLink>
    );
});

NavigationOption.displayName = 'NavigationOption';

export default memo(NavigationOption);
